import {
  type Filter,
  HireType,
  JobLocationContext,
  type JobRelation,
  type JobTravelExpectations,
  type JobWithRelations,
  PayRatePeriod,
  ScreeningAction,
  ScreeningType,
} from '@factoryfixinc/ats-interfaces';
import { useAtsJobApi, useFFRestApi } from '@/composables/useApi';
import { VALID_SHIFTS_FOR_API } from '@/utils/constants';
import { ErrorService } from '@/core/shared/errors/error.service';
import { InternalError } from '@/core/shared/errors/internal.error';
import { InvalidPostalCodeError } from '@/core/shared/errors/invalid-postal-code.error';
import {
  JobValidationError,
  type JobValidationFieldError,
} from '@/core/shared/errors/job-validation.error';
import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import {
  type GenerateJobDescriptionOptions,
  type GenerateScreenerQuestionsOptions,
  type GenerateSkillSuggestionsOptions,
  type GeneratedJobDescriptionDocument,
  type GeneratedScreenerQuestionsDocument,
  type GeneratedSkillSuggestionsDocument,
} from '@/core/shared/job/job.type';
import {
  formatJobDescription,
  getJobSkillsTextForJobDescription,
} from '@/core/jobs/copilot-activation/utils/html-parsing.utils';
import type { ScreeningTypeWithSkip } from '@/core/jobs/copilot-activation/types/local-screener-question.type';

export interface JobLocationCoordinates {
  lat: number;
  lng: number;
}

interface JobLocationResponse {
  jobId: number;
  postalCode: string | null;
  coordinates: JobLocationCoordinates;
}

export class JobPersistence {
  async getById<Relation extends JobRelation>(
    id: number,
    filter: Filter = {},
  ): Promise<JobWithRelations<Relation> | undefined> {
    const encodedFilter = encodeURIComponent(JSON.stringify(filter));
    const path = `/job/${id}?filter=${encodedFilter}`;
    const { data, error, statusCode } = await useFFRestApi(path).json<JobWithRelations<Relation>>();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return undefined;
    }

    if (error.value) {
      throw new InternalError('Could not fetch job', {
        error: error.value,
        status: statusCode.value,
        data: { id },
      });
    }

    return data.value ?? undefined;
  }

  /**
   * Fetches the job's resolved geo coordinates from ff-rest — the canonical job → coords
   * source (also used by ats-conversation). Returns undefined when the job has no
   * resolvable location (404); callers fall back to the human-readable location string.
   */
  async getJobLocation(jobId: number): Promise<JobLocationCoordinates | undefined> {
    const { data, error, statusCode } = await useFFRestApi(
      `/job/${jobId}/location`,
    ).json<JobLocationResponse>();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return undefined;
    }

    if (error.value) {
      throw new InternalError('Could not fetch job location', {
        error: error.value,
        status: statusCode.value,
        data: { jobId },
      });
    }

    return data.value?.coordinates ?? undefined;
  }

  getByIdWithRelations<Relation extends JobRelation>(
    jobId: number,
    relations: Relation[],
  ): Promise<JobWithRelations<Relation> | undefined> {
    const filter: Filter = {
      include: [],
    };

    if (relations.includes('jobTitle' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobTitle' });
    }

    if (relations.includes('employer' as Relation) && filter.include) {
      filter.include.push({ relation: 'employer' });
    }

    if (relations.includes('jobWatcherMaps' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobWatcherMaps' });
    }

    if (relations.includes('employerUserJobWatcherMaps' as Relation) && filter.include) {
      filter.include.push({ relation: 'employerUserJobWatcherMaps' });
    }

    if (relations.includes('jobApplicants' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobApplicants' });
    }

    if (relations.includes('screenerQuestions' as Relation) && filter.include) {
      filter.include.push({ relation: 'screenerQuestions' });
    }

    if (relations.includes('jobStatusHistories' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobStatusHistories' });
    }

    if (relations.includes('jobQualifications' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobQualifications' });
    }

    if (relations.includes('jobResponsibilities' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobResponsibilities' });
    }

    if (relations.includes('jobTaxonomyMachineMaps' as Relation) && filter.include) {
      filter.include.push({
        relation: 'jobTaxonomyMachineMaps',
        scope: {
          include: [
            {
              relation: 'jobTaxonomyMachineMapTaxonomyBrandMaps',
            },
          ],
        },
      });
    }

    if (relations.includes('jobTaxonomyKnowledgeDisciplineMaps' as Relation) && filter.include) {
      filter.include.push({ relation: 'jobTaxonomyKnowledgeDisciplineMaps' });
    }

    return this.getById<Relation>(jobId, filter);
  }

  async generateJobDescription(
    options: GenerateJobDescriptionOptions,
  ): Promise<string | undefined> {
    const path = `/gpt-helper/generate-temporary-job-description`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .post({
        jobTitle: options.title,
        location: options.location,
        skills: options.skills,
        brands: options.brands,
      })
      .json<{ documentId: string }>();

    if (error.value) {
      throw new InternalError('Could not generate job description', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value?.documentId;
  }

  async getGeneratedJobDescription(
    documentId: string,
  ): Promise<GeneratedJobDescriptionDocument | null> {
    const path = `/gpt-helper/get-temporary-job-description/${documentId}`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .get()
      .json<GeneratedJobDescriptionDocument>();

    if (error.value) {
      throw new InternalError('Could not fetch generated job description', {
        error: error.value,
        status: statusCode.value,
        data: { documentId },
      });
    }

    return data.value;
  }

  async generateScreenerQuestions(
    options: GenerateScreenerQuestionsOptions,
  ): Promise<string | undefined> {
    const path = `/gpt-helper/generate-temporary-screening-questions`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .post({
        jobTitle: options.title,
        skills: options.skills,
        brands: options.brands,
        // All three travel together: an absent context means the caller sent none, and the
        // generator falls back to its legacy commute question.
        ...(options.locationContext === undefined
          ? {}
          : {
              locationContext: options.locationContext,
              location: options.location ?? '',
              travelExpectations: options.travelExpectations ?? null,
            }),
      })
      .json<{ documentId: string }>();

    if (error.value) {
      throw new InternalError('Could not generate job description', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value?.documentId;
  }

  async getGeneratedScreenerQuestions(
    documentId: string,
  ): Promise<GeneratedScreenerQuestionsDocument | null> {
    const path = `/gpt-helper/get-temporary-screening-questions/${documentId}`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .get()
      .json<GeneratedScreenerQuestionsDocument>();

    if (error.value) {
      throw new InternalError('Could not fetch generated screener questions', {
        error: error.value,
        status: statusCode.value,
        data: { documentId },
      });
    }

    return data.value;
  }

  async generateSkillSuggestions(options: GenerateSkillSuggestionsOptions): Promise<string> {
    const path = `/gpt-helper/generate-temporary-skill-suggestions`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .post({ jobTitle: options.jobTitle, jobDescription: options.jobDescription })
      .json<{ documentId: string }>();

    if (error.value || !data.value?.documentId) {
      throw new InternalError('Could not generate skill suggestions', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value.documentId;
  }

  async getGeneratedSkillSuggestions(
    documentId: string,
  ): Promise<GeneratedSkillSuggestionsDocument> {
    const path = `/gpt-helper/get-temporary-skill-suggestions/${documentId}`;
    const { data, error, statusCode } = await useAtsJobApi(path)
      .get()
      .json<GeneratedSkillSuggestionsDocument>();

    if (error.value || !data.value) {
      throw new InternalError('Could not fetch generated skill suggestions', {
        error: error.value,
        status: statusCode.value,
        data: { documentId },
      });
    }

    return data.value;
  }

  async updateKnowledgeDisciplines(jobId: number, knowledgeDisciplines: number[]): Promise<void> {
    const path = `/job/${jobId}/taxonomy-knowledge-discipline`;

    const { error, statusCode } = await useFFRestApi(path).put(
      JSON.stringify(knowledgeDisciplines),
    );

    if (error.value) {
      throw new InternalError('Could not update knowledge disciplines', {
        error: error.value,
        status: statusCode.value,
        data: { jobId, knowledgeDisciplines },
      });
    }
  }

  async updateMachineBrands(
    jobId: number,
    machineBrands: { [key: number]: number[] },
  ): Promise<void> {
    const path = `/job/${jobId}/taxonomy-machine`;

    const { error, statusCode } = await useFFRestApi(path).put(JSON.stringify(machineBrands));

    if (error.value) {
      throw new InternalError('Could not update machine brands', {
        error: error.value,
        status: statusCode.value,
        data: { jobId, machineBrands },
      });
    }
  }

  async updateScreenerQuestions(jobId: number, questions: string[]): Promise<void> {
    const path = `/job/${jobId}/screening-questions`;

    const { error, statusCode } = await useFFRestApi(path).put(JSON.stringify(questions));

    if (error.value) {
      throw new InternalError('Could not update screening questions', {
        error: error.value,
        status: statusCode.value,
        data: { jobId, questions },
      });
    }
  }

  async emptyScreenerQuestions(jobId: number): Promise<void> {
    const path = `/job/${jobId}/screening-questions`;

    const { error, statusCode } = await useFFRestApi(path).put(JSON.stringify([]));

    if (error.value) {
      throw new InternalError('Could not empty screening questions', {
        error: error.value,
        status: statusCode.value,
        data: { jobId },
      });
    }
  }

  async updateJob(
    jobId: number,
    options: {
      scheduledUpdates?: boolean;
      activeOutreachEnabled?: boolean;
      knowledgeDisciplines?: number[];
      machineBrands?: { [key: number]: number[] };
      candidateScreening?: {
        employerCareerSiteUrl?: string | null;
        screeningAction?: ScreeningAction | null;
        screeningType?: ScreeningTypeWithSkip | null;
      };
      screenerQuestions?: string[];
      jobDetails?: {
        jobTitleId: number | null;
        street1: string | null;
        city: string | null;
        state: string | null;
        postalCode: string | null;
        // Omitted (undefined) when the Travel Jobs flag is off: an explicit null is a
        // context transition on ff-rest's side, not "unchanged".
        locationContext?: JobLocationContext | null;
        travelExpectations?: JobTravelExpectations | null;
        displayTitle: string | null;
        hireType: HireType | null;
        payRateMin: number | null;
        payRateMax: number | null;
        payRateSalaryMin: number | null;
        payRateSalaryMax: number | null;
        payRatePeriod: PayRatePeriod | null;
        shift: number | null;
        startTime: string | null;
        endTime: string | null;
        anonymous: boolean | null;
        description: string | null;
        rawDescription: string | null;
        knowledgeDisciplines: string[];
        machinesBrands: { title: string; brands: string[] }[];
      };
    },
  ): Promise<void> {
    // Update knowledge disciplines
    if (options.knowledgeDisciplines) {
      await this.updateKnowledgeDisciplines(jobId, options.knowledgeDisciplines);
    }

    // Update machine brands
    if (options.machineBrands) {
      await this.updateMachineBrands(jobId, options.machineBrands);
    }

    // Update screening questions
    if (options.candidateScreening?.screeningType === 'SKIP') {
      await this.emptyScreenerQuestions(jobId);
    } else if (options.screenerQuestions) {
      const onlyFilledQuestions =
        options.screenerQuestions?.filter((question) => question.trim()) ?? [];
      await this.updateScreenerQuestions(jobId, onlyFilledQuestions);
    }

    const jobObject: {
      jobTitleId?: number | null;
      employerCareerSiteUrl?: string | null;
      screeningAction?: ScreeningAction | null;
      screeningType?: ScreeningType;
      street1?: string | null;
      city?: string | null;
      state?: string | null;
      postalCode?: string | null;
      locationContext?: JobLocationContext | null;
      travelExpectations?: JobTravelExpectations | null;
      displayTitle?: string;
      hireType?: HireType | '' | null;
      payRateMin?: number;
      payRateMax?: number;
      payRateSalaryMin?: number;
      payRateSalaryMax?: number;
      payRatePeriod?: string;
      shift?: number | null;
      startTime?: string;
      endTime?: string;
      anonymous?: boolean;
      rawDescription?: string;
      activeOutreachEnabled?: boolean;
    } = {};

    // Handle candidate screening
    if (options.candidateScreening) {
      if (
        options.candidateScreening.screeningAction === ScreeningAction.URL &&
        options.candidateScreening.employerCareerSiteUrl
      ) {
        jobObject.employerCareerSiteUrl = options.candidateScreening.employerCareerSiteUrl;
        jobObject.screeningAction = options.candidateScreening.screeningAction;
      }

      if (options.candidateScreening.screeningAction === ScreeningAction.SCREEN) {
        jobObject.employerCareerSiteUrl = null;
        jobObject.screeningAction = options.candidateScreening.screeningAction;
      }
      jobObject.screeningType = this.mapLocalScreeningTypeToJobScreeningType(
        options.candidateScreening.screeningType,
      );
    } else {
      jobObject.employerCareerSiteUrl = null;
      jobObject.screeningAction = null;
    }

    // Handle job details
    if (options.jobDetails) {
      jobObject.jobTitleId = options.jobDetails.jobTitleId ?? null;
      jobObject.street1 = options.jobDetails.street1?.trim() || null;
      jobObject.city = options.jobDetails.city ?? '';
      jobObject.state = options.jobDetails.state ?? '';
      jobObject.postalCode = options.jobDetails.postalCode ?? '';

      if (options.jobDetails.locationContext !== undefined) {
        const context = options.jobDetails.locationContext;
        jobObject.locationContext = context;
        jobObject.travelExpectations = options.jobDetails.travelExpectations ?? null;

        // Field contexts have no worksite: send the forbidden fields as null so the
        // stored row is cleared instead of tripping ff-rest's per-context 422.
        if (context === JobLocationContext.LIVES_NEAR) {
          jobObject.street1 = null;
          jobObject.postalCode = null;
        } else if (context === JobLocationContext.NATIONWIDE) {
          jobObject.street1 = null;
          jobObject.city = null;
          jobObject.state = null;
          jobObject.postalCode = null;
        }
      }
      jobObject.displayTitle = options.jobDetails.displayTitle ?? '';
      jobObject.hireType = options.jobDetails.hireType ?? null;
      jobObject.payRatePeriod = options.jobDetails.payRatePeriod ?? PayRatePeriod.HOURLY;
      jobObject.anonymous = options.jobDetails.anonymous ?? false;
      jobObject.rawDescription = options.jobDetails.rawDescription ?? '';

      if (jobObject.payRatePeriod === PayRatePeriod.HOURLY) {
        jobObject.payRateMin = Number(options.jobDetails.payRateMin) ?? 0;
        jobObject.payRateMax = Number(options.jobDetails.payRateMax) ?? 0;
      } else {
        jobObject.payRateSalaryMin = Number(options.jobDetails.payRateSalaryMin) ?? 0;
        jobObject.payRateSalaryMax = Number(options.jobDetails.payRateSalaryMax) ?? 0;
      }

      if (VALID_SHIFTS_FOR_API.includes(options.jobDetails.shift)) {
        jobObject.shift = options.jobDetails.shift;
      }

      if (options.jobDetails.shift === 0) {
        jobObject.shift = 0;
        jobObject.startTime = options.jobDetails.startTime ?? '';
        jobObject.endTime = options.jobDetails.endTime ?? '';
      }

      // Handle Description
      const generatedDescription = getJobSkillsTextForJobDescription({
        knowledgeDisciplines: options.jobDetails.knowledgeDisciplines,
        machinesBrands: options.jobDetails.machinesBrands,
      });
      jobObject.rawDescription += generatedDescription;
      jobObject.rawDescription = formatJobDescription(jobObject.rawDescription);
    }

    if (options.activeOutreachEnabled !== undefined) {
      jobObject.activeOutreachEnabled = options.activeOutreachEnabled;
    }

    // Try to update the job
    const path = `/job/${jobId}?scheduledUpdates=${options.scheduledUpdates}`;
    const { error, statusCode, data } = await useFFRestApi(path).patch(JSON.stringify(jobObject));

    if (error.value) {
      // check for special case errors first -- e.g. bad postal code
      if (this.isBadPostalCodeError(statusCode.value, data.value)) {
        throw new InvalidPostalCodeError(jobObject.postalCode ?? '(none)', statusCode.value);
      }

      // 422 with structured per-field details (e.g. address fields exceeding their
      // varchar widths) — surface the bad field(s) to the caller so the toast can
      // tell the customer which field to fix instead of the generic fallback.
      const validationFields = this.parseValidationFields(statusCode.value, data.value);
      if (validationFields) {
        throw new JobValidationError(validationFields, statusCode.value);
      }

      // otherwise, throw a generic error
      throw new InternalError('Could not update job', {
        error: error.value,
        status: statusCode.value,
        data: { jobId, jobObject },
      });
    }
  }

  private parseValidationFields(
    statusCode: number | null,
    data: unknown,
  ): JobValidationFieldError[] | null {
    if (statusCode !== HttpStatus.UNPROCESSABLE_ENTITY) return null;
    if (typeof data !== 'string') return null;
    try {
      const parsed = JSON.parse(data);
      const details = parsed?.error?.details;
      if (!Array.isArray(details)) return null;
      const valid = details.filter(
        (detail: unknown): detail is JobValidationFieldError =>
          detail !== null &&
          typeof detail === 'object' &&
          typeof (detail as { path?: unknown }).path === 'string',
      );
      return valid.length > 0 ? valid : null;
    } catch (error: unknown) {
      ErrorService.captureException(error);
    }
    return null;
  }
  public mapLocalScreeningTypeToJobScreeningType(
    localScreeningType: ScreeningType | 'SKIP' | null | undefined,
  ): ScreeningType | undefined {
    if (localScreeningType === 'SKIP') {
      return ScreeningType.STATIC;
    }
    return localScreeningType || undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isBadPostalCodeError(statusCode: number | null, data: unknown): boolean {
    const badPostalCodePossibleStatusCodes = new Set<number | null>([
      HttpStatus.BAD_REQUEST,
      HttpStatus.UNPROCESSABLE_ENTITY,
    ]);

    try {
      if (badPostalCodePossibleStatusCodes.has(statusCode) && typeof data === 'string') {
        const parsedResponseData = JSON.parse(data);
        return parsedResponseData.error?.message?.includes?.('postal code') === true;
      }
    } catch (error: unknown) {
      // something unexpected happened with parsing the response data
      ErrorService.captureException(error);
    }

    return false;
  }
}
